import React from 'react';
import styled from 'styled-components';

const DEFAULT_CARD_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_CARD_SHADOW = '0 10px 25px rgba(0, 0, 0, 0.13)';
const DEFAULT_CARD_HOVER_SHADOW = '0 18px 35px rgba(0, 0, 0, 0.18)';
const DEFAULT_CARD_BORDER_COLOR = '#dddddd';

const DEFAULT_PRODUCT_NAME_COLOR = '#151515';
const DEFAULT_DESCRIPTION_COLOR = '#333333';
const DEFAULT_PRICE_COLOR = '#df2025';
const DEFAULT_DISCOUNTED_PRICE_COLOR = '#df2025';
const DEFAULT_OLD_PRICE_COLOR = '#777777';
const DEFAULT_INGREDIENTS_COLOR = '#666666';
const DEFAULT_INGREDIENTS_TITLE = 'Ingredienti';

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

function normalizeColor(value, fallback) {
  const normalizedValue = String(value || '').trim();

  return HEX_COLOR_PATTERN.test(normalizedValue) ? normalizedValue : fallback;
}

function normalizeBoolean(value, fallback) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();

    if (normalizedValue === 'true' || normalizedValue === '1') {
      return true;
    }

    if (normalizedValue === 'false' || normalizedValue === '0') {
      return false;
    }
  }

  return fallback;
}

function getFontWeight(value, boldWeight, fallbackWeight) {
  const normalizedValue = normalizeBoolean(value, null);

  if (normalizedValue === null) {
    return fallbackWeight;
  }

  return normalizedValue ? boldWeight : 400;
}

function getFontStyle(value) {
  return normalizeBoolean(value, false) ? 'italic' : 'normal';
}

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function getIngredientNames(ingredients) {
  if (!Array.isArray(ingredients)) {
    return [];
  }

  return [...ingredients]
    .sort((firstIngredient, secondIngredient) => {
      const firstPosition = Number(firstIngredient?.position);
      const secondPosition = Number(secondIngredient?.position);

      const normalizedFirstPosition = Number.isFinite(firstPosition) ? firstPosition : 0;
      const normalizedSecondPosition = Number.isFinite(secondPosition) ? secondPosition : 0;

      return normalizedFirstPosition - normalizedSecondPosition;
    })
    .map((ingredient) => String(ingredient?.name || '').trim())
    .filter(Boolean);
}

const Card = styled.article`
  width: 100%;

  overflow: hidden;

  box-sizing: border-box;

  border: ${({ $showBorder, $borderColor }) => ($showBorder ? `1px solid ${$borderColor}` : '0')};
  border-radius: 10px;

  background: ${({ $backgroundColor }) => $backgroundColor};

  box-shadow: ${({ $cardShadow }) => $cardShadow};

  cursor: pointer;

  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-6px);

    box-shadow: ${({ $hoverShadow }) => $hoverShadow};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;

      box-shadow: ${({ $cardShadow }) => $cardShadow};
    }
  }
`;

const CardImage = styled.div`
  position: relative;

  width: 100%;
  height: 165px;

  overflow: hidden;

  img {
    width: 100%;
    height: 100%;

    display: block;

    object-fit: cover;

    transition: transform 0.4s ease;
  }

  ${Card}:hover & img {
    transform: scale(1.05);
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      transition: none;
    }

    ${Card}:hover & img {
      transform: none;
    }
  }
`;

const ProductBadge = styled.span`
  position: absolute;
  z-index: 2;
  top: 12px;
  left: 12px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  max-width: ${({ $hasAvailabilityBadge }) =>
    $hasAvailabilityBadge ? 'calc(58% - 18px)' : 'calc(100% - 24px)'};
  min-height: 30px;

  padding: 6px 11px;

  overflow: hidden;

  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;

  background: #df2025;

  color: #ffffff;

  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.35px;
  line-height: 1.2;
  text-align: center;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;

  box-shadow:
    0 7px 16px rgba(0, 0, 0, 0.26),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);

  pointer-events: none;
`;

const AvailabilityBadge = styled.span`
  position: absolute;
  z-index: 3;
  top: 12px;
  right: 12px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  max-width: calc(42% - 6px);
  min-height: 30px;

  padding: 6px 10px;

  overflow: hidden;

  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 999px;

  background: rgba(20, 20, 20, 0.92);

  color: #ffffff;

  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.3px;
  line-height: 1.2;
  text-align: center;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;

  box-shadow:
    0 7px 16px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);

  pointer-events: none;
`;

const CardContent = styled.div`
  padding: 14px 16px 18px;
`;

const ProductName = styled.h3`
  margin: 0 0 6px;

  color: ${({ $color }) => $color};

  font-size: 20px;
  font-style: ${({ $fontStyle }) => $fontStyle};
  font-weight: ${({ $fontWeight }) => $fontWeight};
`;

const PriceGroup = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;

  gap: 8px;

  margin-bottom: 8px;
`;

const ProductPrice = styled.strong`
  color: ${({ $color }) => $color};

  font-size: 17px;
  font-style: ${({ $fontStyle }) => $fontStyle};
  font-weight: ${({ $fontWeight }) => $fontWeight};
`;

const OldProductPrice = styled.span`
  color: ${({ $color }) => $color};

  font-size: 14px;
  font-style: ${({ $fontStyle }) => $fontStyle};
  font-weight: ${({ $fontWeight }) => $fontWeight};

  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
`;

const ProductDescription = styled.p`
  margin: 0;

  color: ${({ $color }) => $color};

  font-size: 14px;
  font-style: ${({ $fontStyle }) => $fontStyle};
  font-weight: ${({ $fontWeight }) => $fontWeight};

  line-height: 1.45;
`;

const IngredientsBlock = styled.div`
  margin-top: 10px;

  color: ${({ $color }) => $color};

  font-size: 13px;
  font-style: ${({ $fontStyle }) => $fontStyle};
  font-weight: ${({ $fontWeight }) => $fontWeight};
  line-height: 1.45;
`;

const IngredientsTitle = styled.span`
  margin-right: 4px;
`;

const IngredientsText = styled.span`
  overflow-wrap: anywhere;
`;

function MenuCard({ product, menuSettings }) {
  const cardShadowEnabled = normalizeBoolean(menuSettings?.card_shadow, true);

  const cardShadow = cardShadowEnabled ? DEFAULT_CARD_SHADOW : 'none';
  const cardHoverShadow = cardShadowEnabled ? DEFAULT_CARD_HOVER_SHADOW : 'none';

  const cardBackgroundColor = normalizeColor(
    menuSettings?.card_background_color,
    DEFAULT_CARD_BACKGROUND_COLOR,
  );

  const cardBorderColor = normalizeColor(
    menuSettings?.card_border_color,
    DEFAULT_CARD_BORDER_COLOR,
  );

  const showCardBorder = normalizeBoolean(menuSettings?.show_card_border, false);

  const productNameColor = normalizeColor(
    menuSettings?.product_name_color,
    DEFAULT_PRODUCT_NAME_COLOR,
  );

  const productNameWeight = getFontWeight(menuSettings?.product_name_bold, 800, 800);

  const productNameStyle = getFontStyle(menuSettings?.product_name_italic);

  const descriptionColor = normalizeColor(
    menuSettings?.description_color,
    DEFAULT_DESCRIPTION_COLOR,
  );

  const descriptionWeight = getFontWeight(menuSettings?.description_bold, 700, 500);

  const descriptionStyle = getFontStyle(menuSettings?.description_italic);

  const regularPriceColor = normalizeColor(menuSettings?.price_color, DEFAULT_PRICE_COLOR);

  const discountedPriceColor = normalizeColor(
    menuSettings?.discounted_price_color,
    DEFAULT_DISCOUNTED_PRICE_COLOR,
  );

  const oldPriceColor = normalizeColor(menuSettings?.old_price_color, DEFAULT_OLD_PRICE_COLOR);

  const priceWeight = getFontWeight(menuSettings?.price_bold, 900, 900);

  const priceStyle = getFontStyle(menuSettings?.price_italic);

  const ingredientsTitle = hasText(menuSettings?.ingredients_title)
    ? menuSettings.ingredients_title.trim()
    : DEFAULT_INGREDIENTS_TITLE;

  const ingredientsColor = normalizeColor(
    menuSettings?.ingredients_color,
    DEFAULT_INGREDIENTS_COLOR,
  );

  const ingredientsWeight = getFontWeight(menuSettings?.ingredients_bold, 700, 500);

  const ingredientsStyle = getFontStyle(menuSettings?.ingredients_italic);

  const ingredientNames = getIngredientNames(product?.ingredients);

  const showIngredients = product?.showIngredients === true && ingredientNames.length > 0;

  const hasDiscount =
    Number(product?.discountPercent) > 0 &&
    hasText(product?.discountedPrice) &&
    hasText(product?.originalPrice);

  const badgeText = hasText(product?.badge) ? product.badge.trim() : '';

  const isAvailable = normalizeBoolean(product?.isAvailable, true);
  const isUnavailable = isAvailable === false;

  const imageSource = hasText(product?.image) ? product.image.trim() : null;

  const imageAlt = hasText(product?.name) ? product.name.trim() : 'Prodotto';

  return (
    <Card
      $backgroundColor={cardBackgroundColor}
      $cardShadow={cardShadow}
      $hoverShadow={cardHoverShadow}
      $showBorder={showCardBorder}
      $borderColor={cardBorderColor}
    >
      <CardImage>
        {imageSource && <img src={imageSource} alt={imageAlt} loading="lazy" decoding="async" />}

        {badgeText && (
          <ProductBadge $hasAvailabilityBadge={isUnavailable}>{badgeText}</ProductBadge>
        )}

        {isUnavailable && (
          <AvailabilityBadge aria-label="Prodotto non disponibile">
            Non disponibile
          </AvailabilityBadge>
        )}
      </CardImage>

      <CardContent>
        <ProductName
          $color={productNameColor}
          $fontWeight={productNameWeight}
          $fontStyle={productNameStyle}
        >
          {product.name}
        </ProductName>

        <PriceGroup>
          {hasDiscount ? (
            <>
              <ProductPrice
                $color={discountedPriceColor}
                $fontWeight={priceWeight}
                $fontStyle={priceStyle}
              >
                {product.discountedPrice}
              </ProductPrice>

              <OldProductPrice
                $color={oldPriceColor}
                $fontWeight={priceWeight}
                $fontStyle={priceStyle}
              >
                {product.originalPrice}
              </OldProductPrice>
            </>
          ) : (
            <ProductPrice
              $color={regularPriceColor}
              $fontWeight={priceWeight}
              $fontStyle={priceStyle}
            >
              {product.price}
            </ProductPrice>
          )}
        </PriceGroup>

        <ProductDescription
          $color={descriptionColor}
          $fontWeight={descriptionWeight}
          $fontStyle={descriptionStyle}
        >
          {product.description}
        </ProductDescription>

        {showIngredients && (
          <IngredientsBlock
            $color={ingredientsColor}
            $fontWeight={ingredientsWeight}
            $fontStyle={ingredientsStyle}
          >
            <IngredientsTitle>{ingredientsTitle}:</IngredientsTitle>

            <IngredientsText>{ingredientNames.join(', ')}</IngredientsText>
          </IngredientsBlock>
        )}
      </CardContent>
    </Card>
  );
}

export default MenuCard;
